import get from "lodash/get"
import React from "react"
import {
  ResourceType,
  useLibraryAuthorsQuery,
  useLibraryCategoriesQuery,
  useLibraryResourcesQuery
} from "@pathwright/graphql-client/src/generated"
import { flattenEdges } from "../../utils/apollo"
import { useAlgoliaSearch } from "./AlgoliaProvider"
import ResourcesOrderingFilter from "./BrowseResourceOrderingFilter"
import useControlledLibraryQueryParams from "./useControlledLibraryQueryParams"
import { massageAlgoliaLibraryBrowseResults } from "./utils"

// Context type
interface LibraryResourcesContextType {
  // Resources
  collections: any[]
  courses: any[]
  lessons: any[]
  authors: any[]
  categories: any[]

  // Loading states
  loading: boolean
  searching: boolean

  // Search and filters
  search: string
  debouncedSearch: string
  debouncingSearch: boolean
  handleDebounceSearch: (value: string | null) => void
  selectedAuthorNames: string[]
  setSelectedAuthorNames: React.Dispatch<React.SetStateAction<string[]>>
  selectedCategoryNames: string[]
  setSelectedCategoryNames: React.Dispatch<React.SetStateAction<string[]>>
  readingFromAlgolia: boolean
  viewAllAlgolia: () => Promise<void>
  viewingAll: boolean
  hasMoreAlgolia: boolean

  // Ordering
  orderingFilter: string[]
  setOrderingFilter: (filter: string[]) => void

  // Query objects for LoadMore components
  collectionsQuery: any
  coursesQuery: any
  lessonsQuery: any
  authorsQuery: any
  categoriesQuery: any

  // Other state
  noResults: boolean
  selectedFacetsTotal: number
  controlledQueryParams: any
}

// New: export a hook-only version (no context/provider)
export const useLibraryResources = (): LibraryResourcesContextType => {
  const [orderingFilter, setOrderingFilter] = React.useState(
    ResourcesOrderingFilter.filters.featured
  )

  const {
    search,
    debouncedSearch,
    debouncingSearch,
    handleDebounceSearch,
    selectedAuthorNames,
    setSelectedAuthorNames,
    selectedCategoryNames,
    setSelectedCategoryNames,
    controlledQueryParams
  } = useControlledLibraryQueryParams()

  const {
    results: algoliaResults,
    loading: algoliaLoading,
    algoliaEnabled,
    viewAll: viewAllAlgolia = () => Promise.resolve(),
    viewingAll,
    hasMore: hasMoreAlgolia
  } = useAlgoliaSearch({
    // Memoize to avoid effect re-triggers from changing array identity
    indices: React.useMemo(() => ["library_resource"], []),
    query: search,
    authorFacetValues: selectedAuthorNames,
    categoryFacetValues: selectedCategoryNames,
    massageResults: massageAlgoliaLibraryBrowseResults
  })

  // if algolia is enabled and search has value then search library via algolia, not gql
  const algoliaSearchActivated = algoliaEnabled && search
  // effectively retains gql query data when searching via algolia, better than completely ditching the data via query skip option
  const gqlSearch = algoliaSearchActivated ? null : debouncedSearch || null

  // only filter if there are selected ids
  const categoryNameFilter = selectedCategoryNames.length
    ? { in: selectedCategoryNames }
    : null
  const authorNameFilter = selectedAuthorNames.length
    ? { in: selectedAuthorNames }
    : null

  const facetQueryVariables = {
    search: gqlSearch,
    resourceSearch: gqlSearch,
    resourceAuthorNameFilter: authorNameFilter,
    resourceCategoryNameFilter: categoryNameFilter
  }

  const resourcesQueryVariables = {
    search: gqlSearch,
    categorySearch: gqlSearch,
    authorSearch: gqlSearch,
    categoryNameFilter,
    authorNameFilter,
    orderBy: orderingFilter.join(",") // resources orderBy arg only accepting String for now, but does allow for comma-separated String
  }

  // query for handling non-search & non-algolia querying
  const collectionsQuery = useLibraryResourcesQuery({
    variables: {
      ...resourcesQueryVariables,
      resourceTypeFilter: { eq: ResourceType.Collection },
      first: 2 // yes, 2
    },
    // only drop query data if we have algolia results
    skip: !!algoliaResults
  })

  const coursesQuery = useLibraryResourcesQuery({
    variables: {
      ...resourcesQueryVariables,
      resourceTypeFilter: { eq: ResourceType.Course },
      first: 25
    },
    // only drop query data if we have algolia results
    skip: !!algoliaResults
  })

  const lessonsQuery = useLibraryResourcesQuery({
    variables: {
      ...resourcesQueryVariables,
      resourceTypeFilter: { eq: ResourceType.Lesson },
      first: 25
    },
    // only drop query data if we have algolia results
    skip: !!algoliaResults
  })

  const authorsQuery = useLibraryAuthorsQuery({
    variables: facetQueryVariables,
    // only drop query data if we have algolia results
    skip: !!algoliaResults
  })

  const categoriesQuery = useLibraryCategoriesQuery({
    variables: facetQueryVariables,
    // only drop query data if we have algolia results
    skip: !!algoliaResults
  })

  const gqlResults = {
    collections: flattenEdges(get(collectionsQuery, "data.resources")) || [],
    courses: flattenEdges(get(coursesQuery, "data.resources")) || [],
    lessons: flattenEdges(get(lessonsQuery, "data.resources")) || [],
    authors: flattenEdges(get(authorsQuery, "data.authors")) || [],
    categories: flattenEdges(get(categoriesQuery, "data.categories")) || []
  }

  const { collections, courses, lessons, authors, categories } =
    algoliaResults || gqlResults

  const readingFromAlgolia = !!algoliaResults

  const loading =
    algoliaLoading ||
    collectionsQuery.loading ||
    coursesQuery.loading ||
    lessonsQuery.loading ||
    authorsQuery.loading ||
    categoriesQuery.loading

  const noResults =
    !collections.length &&
    !courses.length &&
    !lessons.length &&
    !collectionsQuery.loading &&
    !coursesQuery.loading &&
    !lessonsQuery.loading &&
    !algoliaLoading

  // searching if search value is currently debounced or if has search value and query is loading
  const searching = debouncingSearch || (!!search && loading)

  const selectedFacetsTotal =
    selectedAuthorNames.length + selectedCategoryNames.length

  return {
    // Resources
    collections,
    courses,
    lessons,
    authors,
    categories,

    // Loading states
    loading,
    searching,

    // Search and filters
    search,
    debouncedSearch,
    debouncingSearch,
    handleDebounceSearch,
    selectedAuthorNames,
    setSelectedAuthorNames,
    selectedCategoryNames,
    setSelectedCategoryNames,
    readingFromAlgolia,
    viewAllAlgolia,
    viewingAll,
    hasMoreAlgolia,

    // Ordering
    orderingFilter,
    setOrderingFilter,

    // Query objects for LoadMore components
    collectionsQuery,
    coursesQuery,
    lessonsQuery,
    authorsQuery,
    categoriesQuery,

    // Other state
    noResults,
    selectedFacetsTotal,
    controlledQueryParams
  }
}
